<script lang="ts" setup>
import { ref } from "vue";
import {
  IonPage,
  IonContent,
  IonInput,
  IonButton,
  IonHeader,
  IonToolbar,
} from "@ionic/vue";
import { useFabriqStore } from "@/store/fabriq";
import { useI18n } from "@/composables/useI18n";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import AppVersion from "@/components/ui/AppVersion.vue";
import BackButton from "@/components/ui/BackButton.vue";
import DownloadLogsButton from "@/components/tools/DownloadLogsButton.vue";
import { nextTick } from "vue";
import SubdomainConfigurationHelp from "@/components/business/SubdomainConfigurationHelp.vue";
import config from "@/config";
import { computed } from "vue";
import { localLogger } from "@/utils/localLogger";

const fabriqStore = useFabriqStore();
const router = useRouter();

const { initializationState, connected, version, intercomEnabled } =
  storeToRefs(fabriqStore);

const { t } = useI18n();

const loading = ref(false);
const showHelp = ref(true);
const subdomain = ref("");
const fullDomain = computed(
  () => `${config.protocol}${subdomain.value}.${config.domain}`
);

fabriqStore.setInitializationState({ type: "done" });

async function handleConfigureSubdomain() {
  if (subdomain.value) {
    await localLogger.log(`QR code scanned ${subdomain.value}`);
    loading.value = true;
    try {
      fabriqStore.setInitializationState({ type: "pending" });
      await nextTick();
      await fabriqStore.initializeApiClients(fullDomain.value);
      router.replace({ name: "Login" });
    } catch (e) {
      loading.value = false;
      await localLogger.log(
        `Error while configuring application from QR code ${JSON.stringify(e)}`
      );
    }
  }
}

function handleBack() {
  console.log("showHelp.value", showHelp.value);
  if (showHelp.value) {
    router.push("ApplicationConfigurationIndex");
  } else {
    showHelp.value = true;
  }
}

const showPage = computed(() => {
  return initializationState.value.type === "done" && !connected;
});
</script>
<template>
  <ion-page>
    <ion-header mode="ios">
      <ion-toolbar class="transparent-toolbar ion-padding-start" mode="ios">
        <div class="page-toolbar">
          <back-button @back="handleBack" :disabled="loading" />
        </div>
      </ion-toolbar>
    </ion-header>
    <ion-content :fullscreen="true" :scroll-y="false" class="hide-scrollbar">
      <subdomain-configuration-help
        v-if="!showPage && showHelp"
        @configure="showHelp = false"
      />
      <div class="login-block" v-if="!showPage && !showHelp">
        <div class="fabriq-logo"></div>
        <div class="ion-margin-bottom">
          {{ t("common.subdomainTitle") }}
        </div>
        <div class="fabriq-input-wrapper">
          <div class="fabriq-input-protocol hint">
            {{ config.protocol }}
          </div>
          <ion-input
            autofocus
            class="fabriq-input"
            inputmode="url"
            pattern="url"
            enterkeyhint="next"
            v-model="subdomain"
            @keypress.enter="handleConfigureSubdomain"
            type="email"
            name="subdomain"
            required
            :placeholder="t('common.subdomain')"
          />
          <div class="fabriq-input-domain hint">.{{ config.domain }}</div>
        </div>
        <div class="hint" v-if="!subdomain?.length">
          {{ t("common.eg") }} {{ config.protocol
          }}<strong>subdomain</strong>.{{ config.domain }}
        </div>
        <div class="hint" v-else>
          {{ fullDomain }}
        </div>
        <ion-button
          class="ion-margin-top"
          color="secondary"
          expand="block"
          mode="ios"
          size="small"
          :disabled="!subdomain?.length || loading"
          @click="handleConfigureSubdomain"
        >
          <div class="app-configuration-button-content" v-if="loading">
            <font-icon color="#ffffff" name="spinner" spin size="0.3" />
            <div>
              {{ t("common.loading") }}
            </div>
          </div>
          <div v-else>{{ t("apply") }}</div>
        </ion-button>
      </div>
      <app-version v-if="!showHelp" :version="version" position="absolute" />
    </ion-content>
    <download-logs-button v-if="!showHelp" />
    <div
      v-if="!showHelp && intercomEnabled"
      class="get-support-button"
      @click="fabriqStore.openIntercomChat"
    >
      {{ t("common.getSupportLite") }}
    </div>
  </ion-page>
</template>

<style scoped>
.login-block {
  position: relative;
  margin: auto;
  margin-top: 3rem;
  width: calc(100% - 84px);
  text-align: center;
}

.login-block .fabriq-logo {
  margin-bottom: calc(var(--ion-padding) * 4);
}

.fabriq-input-wrapper {
  text-align: left;
  font-size: var(--font-size-m);
  padding: calc(var(--ion-padding) / 3 - 2px) var(--ion-padding);
  background-color: var(--ion-color-primary-contrast);
  border: 1px solid var(--ion-border-color);
  border-radius: var(--f-border-radius);
  color: var(--ion-color-primary);
  margin-bottom: var(--ion-padding);
  margin-inline: 2px;
  display: flex;
  align-items: center;
}

.fabriq-input-wrapper::part(native) {
  --inner-border-width: 0;
}

.fabriq-input-protocol {
  width: 50px;
}

.fabriq-input-domain {
  width: 75px;
  text-align: right;
}

.fabriq-input {
  flex: 1;
}

ion-button {
  font-size: var(--font-size-m);
  height: 2.25rem;
}

ion-button::part(native) {
  border-radius: 5px;
}
</style>
